(function () {
  'use strict';

  var DESKTOP_MQ = window.matchMedia('(min-width: 768px)');
  // Distance the rotated 12px pointer protrudes beyond the card edge (6 * √2, rounded up)
  var ARROW_PROTRUSION = 9;
  // Visible gap between the pointer tip and the hotspot, plus the pointer itself
  var CARD_GAP = 8 + ARROW_PROTRUSION;
  // Closest the pointer centre may sit to a card corner (card radius + pointer half-diagonal)
  var ARROW_INSET = 4 + ARROW_PROTRUSION;
  var MOBILE_CARD_GAP = 8;
  // Minimum gap kept between the card and the image edges
  var EDGE_MARGIN = 16;
  var instances = {};

  function clamp(value, min, max) {
    if (max < min) return min;
    return Math.min(Math.max(value, min), max);
  }

  function ShopTheLook(container) {
    this.container = container;
    this.media = container.querySelector('[data-stl-media]');
    this.hotspots = Array.prototype.slice.call(container.querySelectorAll('[data-stl-hotspot]'));
    this.activeHotspot = null;
    this.resizeFrame = null;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);

    this.bindEvents();
  }

  ShopTheLook.prototype.bindEvents = function () {
    var self = this;

    this.hotspots.forEach(function (hotspot) {
      hotspot.addEventListener('click', function (event) {
        event.preventDefault();
        self.toggle(hotspot);
      });
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);

    if (DESKTOP_MQ.addEventListener) {
      DESKTOP_MQ.addEventListener('change', this.onResize);
    } else if (DESKTOP_MQ.addListener) {
      DESKTOP_MQ.addListener(this.onResize);
    }
  };

  ShopTheLook.prototype.destroy = function () {
    this.close();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);

    if (DESKTOP_MQ.removeEventListener) {
      DESKTOP_MQ.removeEventListener('change', this.onResize);
    } else if (DESKTOP_MQ.removeListener) {
      DESKTOP_MQ.removeListener(this.onResize);
    }
  };

  ShopTheLook.prototype.getCard = function (hotspot) {
    return document.getElementById(hotspot.getAttribute('aria-controls'));
  };

  ShopTheLook.prototype.toggle = function (hotspot) {
    if (this.activeHotspot === hotspot) {
      this.close();
    } else {
      this.open(hotspot);
    }
  };

  ShopTheLook.prototype.open = function (hotspot) {
    var card = this.getCard(hotspot);
    if (!card) return;

    if (this.activeHotspot) {
      this.close();
    }

    hotspot.setAttribute('aria-expanded', 'true');
    card.hidden = false;
    this.activeHotspot = hotspot;
    this.position();
  };

  ShopTheLook.prototype.close = function (returnFocus) {
    var hotspot = this.activeHotspot;
    if (!hotspot) return;

    var card = this.getCard(hotspot);

    hotspot.setAttribute('aria-expanded', 'false');
    if (card) {
      card.hidden = true;
      this.resetCardPosition(card);
    }
    this.media.style.marginBottom = '';
    this.activeHotspot = null;

    if (returnFocus) {
      hotspot.focus();
    }
  };

  ShopTheLook.prototype.resetCardPosition = function (card) {
    card.style.left = '';
    card.style.top = '';
    card.style.removeProperty('--stl-arrow-offset');
    card.removeAttribute('data-placement');
  };

  ShopTheLook.prototype.position = function () {
    var hotspot = this.activeHotspot;
    if (!hotspot) return;

    var card = this.getCard(hotspot);
    if (!card) return;

    this.resetCardPosition(card);

    // Mobile: card sits below the image, so reserve room for it in the flow
    if (!DESKTOP_MQ.matches) {
      this.media.style.marginBottom = card.offsetHeight + MOBILE_CARD_GAP + 'px';
      return;
    }

    this.media.style.marginBottom = '';

    var mediaRect = this.media.getBoundingClientRect();
    var hotspotRect = hotspot.querySelector('.shop-the-look__hotspot-dot').getBoundingClientRect();
    var cardWidth = card.offsetWidth;
    var cardHeight = card.offsetHeight;
    // Hotspot centre and radius relative to the media wrapper
    var radius = hotspotRect.width / 2;
    var centreX = hotspotRect.left + radius - mediaRect.left;
    var centreY = hotspotRect.top + hotspotRect.height / 2 - mediaRect.top;

    // Room is measured to the image edges so the card never spills outside the image
    var space = {
      right: mediaRect.width - centreX - radius - EDGE_MARGIN,
      left: centreX - radius - EDGE_MARGIN,
      bottom: mediaRect.height - centreY - radius,
      top: centreY - radius,
    };

    // Prefer beside the hotspot (right, then left) when the card can be centred on it vertically;
    // near the top edge go below, near the bottom edge go above
    var fitsBeside = centreY - cardHeight / 2 >= 0 && centreY + cardHeight / 2 <= mediaRect.height;
    var fitsRight = space.right >= cardWidth + CARD_GAP;
    var fitsLeft = space.left >= cardWidth + CARD_GAP;
    var placement;

    if (fitsBeside && fitsRight) {
      placement = 'right';
    } else if (fitsBeside && fitsLeft) {
      placement = 'left';
    } else if (space.bottom >= cardHeight + CARD_GAP && space.bottom >= space.top) {
      placement = 'bottom';
    } else if (space.top >= cardHeight + CARD_GAP) {
      placement = 'top';
    } else if (space.bottom >= cardHeight + CARD_GAP) {
      placement = 'bottom';
    } else {
      placement = space.right >= space.left ? 'right' : 'left';
    }

    var left;
    var top;
    var arrowOffset;

    if (placement === 'right' || placement === 'left') {
      left = placement === 'right'
        ? centreX + radius + CARD_GAP
        : centreX - radius - CARD_GAP - cardWidth;
      left = clamp(left, EDGE_MARGIN, mediaRect.width - cardWidth - EDGE_MARGIN);
      top = clamp(centreY - cardHeight / 2, 0, mediaRect.height - cardHeight);
      arrowOffset = clamp(centreY - top, ARROW_INSET, cardHeight - ARROW_INSET);
    } else {
      top = placement === 'bottom'
        ? centreY + radius + CARD_GAP
        : centreY - radius - CARD_GAP - cardHeight;
      left = clamp(centreX - cardWidth / 2, EDGE_MARGIN, mediaRect.width - cardWidth - EDGE_MARGIN);
      arrowOffset = clamp(centreX - left, ARROW_INSET, cardWidth - ARROW_INSET);
    }

    card.setAttribute('data-placement', placement);
    card.style.left = Math.round(left) + 'px';
    card.style.top = Math.round(top) + 'px';
    card.style.setProperty('--stl-arrow-offset', Math.round(arrowOffset) + 'px');
  };

  ShopTheLook.prototype.onDocumentClick = function (event) {
    if (!this.activeHotspot || !DESKTOP_MQ.matches) return;

    var card = this.getCard(this.activeHotspot);
    if (card && card.contains(event.target)) return;
    if (this.activeHotspot.contains(event.target)) return;

    // Clicks on another hotspot in this section are handled by toggle()
    var hotspot = event.target.closest && event.target.closest('[data-stl-hotspot]');
    if (hotspot && this.container.contains(hotspot)) return;

    this.close();
  };

  ShopTheLook.prototype.onKeydown = function (event) {
    if (!this.activeHotspot) return;
    if (event.key !== 'Escape' && event.keyCode !== 27) return;

    this.close(true);
  };

  ShopTheLook.prototype.onResize = function () {
    if (!this.activeHotspot) return;

    var self = this;
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(function () {
      self.resizeFrame = null;
      self.position();
    });
  };

  function init(scope) {
    var containers = (scope || document).querySelectorAll('[data-shop-the-look]');

    Array.prototype.forEach.call(containers, function (container) {
      var sectionId = container.getAttribute('data-section-id');
      if (instances[sectionId]) return;
      instances[sectionId] = new ShopTheLook(container);
    });
  }

  function destroy(sectionId) {
    if (!instances[sectionId]) return;
    instances[sectionId].destroy();
    delete instances[sectionId];
  }

  function findHotspot(sectionId, blockId) {
    var instance = instances[sectionId];
    if (!instance) return null;

    for (var i = 0; i < instance.hotspots.length; i++) {
      if (instance.hotspots[i].getAttribute('data-block-id') === blockId) {
        return { instance: instance, hotspot: instance.hotspots[i] };
      }
    }
    return null;
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    destroy(event.detail.sectionId);
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.detail.sectionId);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var match = findHotspot(event.detail.sectionId, event.detail.blockId);
    if (match) match.instance.open(match.hotspot);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var match = findHotspot(event.detail.sectionId, event.detail.blockId);
    if (match && match.instance.activeHotspot === match.hotspot) match.instance.close();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
